if (!customElements.get('product-options')) {
    class ProductOptions extends HTMLElement {
        constructor() {
            super();
            this.productHandle = null;
            this.productId = null;
            this.currentVariant = null;
            this.productOptions = null;
            this.variantSelector = null;
            this.quantityInputs = [];
            this.optionInputs = [];
            this.selectedOptions = {};
            this.variantTiers = null;
            this.ignoreVariantsMetafields = false;
            this.quantityLabelMultiplier = 1;
            this.quantityUnitLabel = 'Pack';

            this.selectedQuantity = null;
            this._cachedIsRelatedVariant = false;

            // Pre-bind handlers once for symmetric add/remove
            this._onOptionChange = this.handleOptionChange.bind(this);
            this._onQtyChange = this.handleQuantityChange.bind(this);

            // Broadcast/listen for cross-instance sync
            this._onExternalSelection = this.onExternalSelection.bind(this);
            this._onMirrorRequest = this.handleMirrorRequest.bind(this);
            this._isSyncing = false;
            this._isUpdatingQuantityPicker = false;

            // Scope container for isolating updates (modal/drawer vs main page)
            this.scopeContainer = null;
        }

        connectedCallback() {
            try {
                // Find scope container for this component
                this.scopeContainer = this.findScopeContainer();

                this.initialize();
                this.setupEventListeners();
                this.setupMirroring();

                // Register global listener once per instance
                document.addEventListener('product-options:changed', this._onExternalSelection);
                
                // ORIGINAL forms listen for requests from MIRROR forms
                if (!this.mirrorSourceEl) {
                    document.addEventListener('product-options:mirror-request', this._onMirrorRequest);
                }
            } catch (error) {
                throw error;
            }
        }

        disconnectedCallback() {
            this.cleanup();
        }

        /**
         * Finds the scoped container for this element.
         * Scope is either product-card or null (main page)
         */
        findScopeContainer() {
            return this.closest('product-card') || null;
        }

        /**
         * Checks if an element is in the same scope as this component
         */
        isInSameScope(element) {
            if (!element) return false;
            const otherScope = element.closest('product-card') || null;
            return this.scopeContainer === otherScope;
        }

        /**
         * Query elements scoped to the same container as this component
         */
        queryScopedElements(selector) {
            if (this.scopeContainer) {
                // We're in a scoped container - only search within it
                return Array.from(this.scopeContainer.querySelectorAll(selector));
            }

            // We're in the main document - search globally but exclude scoped containers
            const allElements = Array.from(document.querySelectorAll(selector));
            return allElements.filter(el => this.isInSameScope(el));
        }

        // --- Mirroring ---
        setupMirroring() {
            const sourceId = (this.dataset.mirrorSource || '').trim();
            if (!sourceId) return;

            const tryAttach = () => {
                const src = document.getElementById(sourceId);
                if (src && src !== this) {
                    this.attachToSource(src);
                    // If we were observing for late mount, stop observing now
                    if (this._mirrorObserver) {
                        this._mirrorObserver.disconnect();
                        this._mirrorObserver = null;
                    }
                    if (this._mirrorObserverTimeoutId) {
                        clearTimeout(this._mirrorObserverTimeoutId);
                        this._mirrorObserverTimeoutId = null;
                    }
                    return true;
                }
                return false;
            };

            if (tryAttach()) return;

            // Observe DOM for late-mounted source (e.g., quick-buy modal)
            const observer = new MutationObserver(() => {
                if (tryAttach()) {
                    observer.disconnect();
                }
            });
            this._mirrorObserver = observer;
            observer.observe(document.documentElement, { childList: true, subtree: true });

            // Safety: auto-disconnect after 30s if source never appears to avoid leaks
            this._mirrorObserverTimeoutId = setTimeout(() => {
                if (this._mirrorObserver) {
                    this._mirrorObserver.disconnect();
                    this._mirrorObserver = null;
                }
                this._mirrorObserverTimeoutId = null;
            }, 30000);
        }

        attachToSource(sourceEl) {
            this.mirrorSourceEl = sourceEl;

            // Prefer listening on the parent form to reduce complexity
            const sourceForm = sourceEl.closest('form');
            this._mirrorSourceForm = sourceForm || null;

            // Initial sync from source state
            this.syncFromSource();

            const handler = this.onSourceChanged.bind(this);
            this._mirrorFormHandler = handler;
            if (this._mirrorSourceForm) {
                this._mirrorSourceForm.addEventListener('change', handler);
                this._mirrorSourceForm.addEventListener('input', handler);
            } else {
                // Fallback: listen directly on the source element
                sourceEl.addEventListener('change', handler);
                this._mirrorElHandler = handler;
            }
        }

        onSourceChanged() {
            this.syncFromSource();
        }

        syncFromSource() {
            const src = this.mirrorSourceEl;
            if (!src) return;

            // Set syncing flag to prevent cascades from mirror sync
            this._isSyncing = true;
            try {
                const container = this._mirrorSourceForm || src;
                const checked = container.querySelectorAll('input[type="radio"]:checked');
                checked.forEach(srcInput => {
                    const name = srcInput.name;
                    if (name === 'quantity') {
                        const targetQty = this.querySelector(`input[name="quantity"][value="${CSS.escape(srcInput.value)}"]`);
                        if (targetQty && !targetQty.checked && !targetQty.disabled) {
                            targetQty.checked = true;
                            targetQty.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                        return;
                    }
                    if (name.includes('-')) {
                        const optionName = this.getOptionNameFromInput(srcInput);
                        const value = srcInput.value;
                        const target = this.querySelector(`input[type="radio"][name^="${CSS.escape(optionName)}-"][value="${CSS.escape(value)}"]`);
                        if (target && !target.checked && !target.disabled) {
                            target.checked = true;
                            target.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                    }
                });
            } finally {
                this._isSyncing = false;
            }
        }

        cleanup() {
            // Remove event listeners
            this.optionInputs.forEach(input => {
                input.removeEventListener('change', this._onOptionChange);
            });
            this.quantityInputs.forEach(input => {
                input.removeEventListener('change', this._onQtyChange);
            });

            // Detach mirroring listeners
            if (this._mirrorSourceForm && this._mirrorFormHandler) {
                this._mirrorSourceForm.removeEventListener('change', this._mirrorFormHandler);
                this._mirrorSourceForm.removeEventListener('input', this._mirrorFormHandler);
            }
            if (this.mirrorSourceEl && this._mirrorElHandler) {
                this.mirrorSourceEl.removeEventListener('change', this._mirrorElHandler);
            }
            this._mirrorSourceForm = null;
            this._mirrorFormHandler = null;
            this._mirrorElHandler = null;

            // Disconnect observer and clear timeout if still active
            if (this._mirrorObserver) {
                this._mirrorObserver.disconnect();
                this._mirrorObserver = null;
            }
            if (this._mirrorObserverTimeoutId) {
                clearTimeout(this._mirrorObserverTimeoutId);
                this._mirrorObserverTimeoutId = null;
            }
            
            // Disconnect product-card observer
            if (this._productCardObserver) {
                this._productCardObserver.disconnect();
                this._productCardObserver = null;
            }
            if (this._productCardUpdateTimeout) {
                clearTimeout(this._productCardUpdateTimeout);
                this._productCardUpdateTimeout = null;
            }

            document.removeEventListener('product-options:changed', this._onExternalSelection);
            document.removeEventListener('product-options:mirror-request', this._onMirrorRequest);

            document.body.classList.remove('product-is-alternate');
        }

        initialize() {
            this.productHandle = this.dataset.productHandle;
            this.productId = this.dataset.productId;
            
            // Parse variant tiers from data attribute
            const variantTiersData = this.dataset.variantTiers;
            if (variantTiersData) {
                try {
                    const parsed = JSON.parse(variantTiersData);
                    this.variantTiers = Array.isArray(parsed) ? parsed : [];
                } catch (e) {
                    this.variantTiers = [];
                }
            }
            
            // Get quantity label settings
            this.quantityLabelMultiplier = parseInt(this.dataset.quantityLabelMultiplier || '1', 10);
            this.quantityUnitLabel = this.dataset.quantityUnitLabel || 'Pack';

            // Get pack numbers for bi-directional tier support
            this.productPackNumber = parseInt(this.dataset.productPackNumber || '1', 10);
            this.relatedProductPackNumber = this.dataset.relatedProductPackNumber
                ? parseInt(this.dataset.relatedProductPackNumber, 10)
                : null;
            this.relatedProductId = this.dataset.relatedProductId
                ? parseInt(this.dataset.relatedProductId, 10)
                : null;
            this.relatedProductHandle = (this.dataset.relatedProductHandle || '').trim() || null;

            // Find the variant selector
            this.variantSelector = this.querySelector('select[name="id"]');
            if (!this.variantSelector) {
                return;
            }

            // Get current variant
            this.currentVariant = this.getCurrentVariant();

            // Check URL for variant override (crucial for related products)
            const urlParams = new URLSearchParams(window.location.search);
            const urlVariantId = urlParams.get('variant');
            if (urlVariantId && this.variantSelector) {
                const urlVariantIdInt = parseInt(urlVariantId, 10);
                const currentSelectorValue = parseInt(this.variantSelector.value, 10);
                
                if (urlVariantIdInt && urlVariantIdInt !== currentSelectorValue) {
                     // Try to find this variant in options
                     const matchingOption = this.variantSelector.querySelector(`option[value="${urlVariantIdInt}"]`);
                     if (matchingOption) {
                         this.variantSelector.value = urlVariantIdInt;
                         // Update current variant reference immediately
                         this.currentVariant = {
                             id: urlVariantIdInt,
                             available: !matchingOption.disabled,
                             option: matchingOption
                         };
                         // Dispatch change event so other components (Images, Price) update their state
                         this.variantSelector.dispatchEvent(new Event('change', { bubbles: true }));
                     }
                }
            } else {
                // If no URL variant, ensure currentVariant is set from selector
                if (this.variantSelector && this.variantSelector.value) {
                    const selectedOption = this.variantSelector.querySelector('option:checked') || 
                                         this.variantSelector.querySelector(`option[value="${this.variantSelector.value}"]`);
                    if (selectedOption) {
                        this.currentVariant = {
                            id: parseInt(this.variantSelector.value, 10),
                            available: !selectedOption.disabled,
                            option: selectedOption
                        };
                    }
                }
            }
            
            // Initialize option inputs
            this.optionInputs = Array.from(this.querySelectorAll('input[type="radio"][name*="-"]'));
            this.quantityInputs = Array.from(this.querySelectorAll('input[type="radio"][name="quantity"]'));
            
            // Initialize selected options
            this.updateSelectedOptions();
            
            // Listen for variant selector changes to update quantity picker
            if (this.variantSelector) {
                this.variantSelector.addEventListener('change', this.handleVariantSelectorChange.bind(this));
                
                // Update quantity picker with initial variant
                const initialVariantId = parseInt(this.variantSelector.value);
                if (initialVariantId) {
                    // Use setTimeout to ensure DOM is fully rendered
                    setTimeout(() => {
                        // Check if initial variant is out of stock
                        const initialVariantOption = this.variantSelector.querySelector(`option[value="${initialVariantId}"]`);
                        const isInitialVariantOutOfStock = initialVariantOption && (
                            initialVariantOption.dataset.maxQuantity && parseInt(initialVariantOption.dataset.maxQuantity, 10) <= 0 && 
                            initialVariantOption.dataset.inventoryPolicy === 'deny'
                        );
                        
                        // If initial variant is out of stock, optionally cascade to find first available variant
                        // (when data-prioritise-in-stock is not "false"). Preserves option1 if possible and visible options.
                        const prioritiseInStock = this.dataset.prioritiseInStock !== 'false';
                        if (isInitialVariantOutOfStock && prioritiseInStock) {
                            const allVariants = Array.from(this.variantSelector.options);
                            
                            // Get the initial variant's option1 value to preserve it
                            const optionGroups = this.getOptionGroups();
                            let initialOption1Value = null;
                            
                            if (optionGroups.length > 0 && initialVariantOption) {
                                // Try to get option1 from data attributes
                                const firstOptionName = optionGroups[0].name;
                                const dataAttr = firstOptionName.toLowerCase().replace(/\s+/g, '_');
                                const camelCaseAttr = dataAttr.split('_').map((word, index) => {
                                    if (index === 0) return word;
                                    return word.charAt(0).toUpperCase() + word.slice(1);
                                }).join('');
                                
                                initialOption1Value = initialVariantOption.dataset[camelCaseAttr] || 
                                                     initialVariantOption.getAttribute(`data-${dataAttr}`);
                                
                                // Fallback: parse from variant title
                                if (!initialOption1Value) {
                                    const variantTitle = initialVariantOption.text.trim();
                                    const variantParts = variantTitle.split(' / ').map(part => part.trim());
                                    if (variantParts.length > 0) {
                                        initialOption1Value = variantParts[0];
                                    }
                                }
                            }
                            
                            // Helper function to check if variant is in stock
                            const isVariantInStock = (opt) => {
                                const maxQty = opt.dataset.maxQuantity ? parseInt(opt.dataset.maxQuantity, 10) : null;
                                const inventoryPolicy = opt.dataset.inventoryPolicy;
                                return (maxQty === null || maxQty > 0 || inventoryPolicy !== 'deny');
                            };
                            
                            // Helper function to get option1 value from variant option element
                            const getOption1Value = (opt) => {
                                if (optionGroups.length === 0) return null;
                                const firstOptionName = optionGroups[0].name;
                                const dataAttr = firstOptionName.toLowerCase().replace(/\s+/g, '_');
                                const camelCaseAttr = dataAttr.split('_').map((word, index) => {
                                    if (index === 0) return word;
                                    return word.charAt(0).toUpperCase() + word.slice(1);
                                }).join('');
                                
                                let value = opt.dataset[camelCaseAttr] || opt.getAttribute(`data-${dataAttr}`);
                                if (!value) {
                                    const variantTitle = opt.text.trim();
                                    const variantParts = variantTitle.split(' / ').map(part => part.trim());
                                    if (variantParts.length > 0) {
                                        value = variantParts[0];
                                    }
                                }
                                return value;
                            };
                            
                            // CRITICAL: Check which option inputs are currently visible/enabled (filtered variants)
                            // Only allow switching to variants that match visible options
                            const getVisibleOptionValues = () => {
                                const visibleValues = {};
                                optionGroups.forEach(group => {
                                    // Get all visible (not hidden) inputs for this option group
                                    const visibleInputs = group.inputs.filter(input => {
                                        const label = input.closest('label');
                                        if (!label) return false;
                                        // Check if input/label is visible (not in hidden container, not display:none)
                                        const isHidden = label.closest('.hidden') || 
                                                        label.closest('[style*="display: none"]') ||
                                                        window.getComputedStyle(label).display === 'none';
                                        return !isHidden && !input.disabled;
                                    });
                                    if (visibleInputs.length > 0) {
                                        visibleValues[group.name] = visibleInputs.map(input => input.value);
                                    }
                                });
                                return visibleValues;
                            };
                            
                            const visibleOptionValues = getVisibleOptionValues();
                            
                            // Helper function to check if a variant matches visible/filtered options
                            const variantMatchesVisibleOptions = (opt) => {
                                // If no visible options are filtered, all variants are available
                                if (Object.keys(visibleOptionValues).length === 0) return true;
                                
                                // Check if variant's options match visible option values
                                for (let i = 0; i < optionGroups.length; i++) {
                                    const group = optionGroups[i];
                                    const visibleValues = visibleOptionValues[group.name];
                                    
                                    // If this option group has visible values, variant must match one
                                    if (visibleValues && visibleValues.length > 0) {
                                        const dataAttr = group.name.toLowerCase().replace(/\s+/g, '_');
                                        const camelCaseAttr = dataAttr.split('_').map((word, index) => {
                                            if (index === 0) return word;
                                            return word.charAt(0).toUpperCase() + word.slice(1);
                                        }).join('');
                                        
                                        const variantValue = opt.dataset[camelCaseAttr] || opt.getAttribute(`data-${dataAttr}`);
                                        
                                        // If variant value doesn't match any visible value, exclude it
                                        if (variantValue && !visibleValues.includes(variantValue)) {
                                            return false;
                                        }
                                        
                                        // Fallback: parse from variant title
                                        if (!variantValue) {
                                            const variantTitle = opt.text.trim();
                                            const variantParts = variantTitle.split(' / ').map(part => part.trim());
                                            if (i < variantParts.length) {
                                                const variantPart = variantParts[i];
                                                if (!visibleValues.includes(variantPart)) {
                                                    return false;
                                                }
                                            }
                                        }
                                    }
                                }
                                return true;
                            };
                            
                            // Find an in-stock variant with the same option1 AND matching visible options
                            let firstInStockVariant = null;
                            if (initialOption1Value) {
                                firstInStockVariant = allVariants.find(opt => {
                                    if (!isVariantInStock(opt)) return false;
                                    const opt1Value = getOption1Value(opt);
                                    if (opt1Value !== initialOption1Value) return false;
                                    // CRITICAL: Must also match visible/filtered options
                                    return variantMatchesVisibleOptions(opt);
                                });
                            }
                            
                            // Only switch variant if we found an in-stock variant with the same option1 AND matching visible options
                            // If all variants with option1 are out of stock OR don't match visible options, keep the original selection
                            if (firstInStockVariant) {
                                // Select the first in-stock variant
                                this.variantSelector.value = firstInStockVariant.value;
                                
                                // Update selected options to match the new variant
                                // First try to use data attributes if available
                                let usedDataAttributes = false;
                                
                                // Try to match using data attributes first
                                for (let i = 0; i < optionGroups.length; i++) {
                                    const group = optionGroups[i];
                                    const optionName = group.name;
                                    const dataAttr = optionName.toLowerCase().replace(/\s+/g, '_');
                                    const camelCaseAttr = dataAttr.split('_').map((word, index) => {
                                        if (index === 0) return word;
                                        return word.charAt(0).toUpperCase() + word.slice(1);
                                    }).join('');
                                    
                                    const variantValue = firstInStockVariant.dataset[camelCaseAttr] || 
                                                         firstInStockVariant.getAttribute(`data-${dataAttr}`);
                                    
                                    if (variantValue && variantValue !== '') {
                                        usedDataAttributes = true;
                                        // Find and select the matching option input
                                        const matchingInput = group.inputs.find(input => input.value === variantValue);
                                        if (matchingInput) {
                                            matchingInput.checked = true;
                                            this.selectedOptions[optionName] = variantValue;
                                        }
                                    }
                                }
                                
                                // Fallback: parse variant title if data attributes weren't available
                                if (!usedDataAttributes) {
                                    const variantTitle = firstInStockVariant.text.trim();
                                    const variantParts = variantTitle.split(' / ').map(part => part.trim());
                                    
                                    // Match each variant part to the corresponding option group by position
                                    optionGroups.forEach((group, groupIndex) => {
                                        if (groupIndex < variantParts.length) {
                                            const variantPart = variantParts[groupIndex];
                                            // Find the option input with matching value
                                            const matchingInput = group.inputs.find(input => input.value === variantPart);
                                            if (matchingInput) {
                                                matchingInput.checked = true;
                                                this.selectedOptions[group.name] = variantPart;
                                            }
                                        }
                                    });
                                }
                                
                                // Update selected options state
                                this.updateSelectedOptions();
                                
                                // Trigger change event to update UI
                                this.variantSelector.dispatchEvent(new Event('change', { bubbles: true }));
                                
                                // Update variant selector to trigger all related updates
                                this.updateVariantSelector();
                            }
                        }
                        
                        // Update quantity picker with current variant (may have changed)
                        const currentVariantId = parseInt(this.variantSelector.value);
                        if (currentVariantId) {
                            this.updateQuantityPicker(currentVariantId);
                        }
                        
                        // Update out-of-stock options and add to cart button on initial load
                        const selectedOptions = this.getSelectedOptions();
                        const matchingVariant = this.findMatchingVariant(selectedOptions);
                        
                        // Always update out-of-stock options, regardless of whether variant is found
                            this.updateOutOfStockOptions();
                        
                        if (matchingVariant) {
                            this.updateAddToCartButton(matchingVariant);
                        }
                    }, 0);
                } else {
                    // Even if no initial variant, update out-of-stock options
                    setTimeout(() => {
                        this.updateOutOfStockOptions();
                    }, 0);
                }
            }
            
            // Initialize product-card images on page load
            setTimeout(() => {
                this.updateProductCardMediaBlocks();
            }, 0);
            
            // Set up mutation observer to watch for product-card data-variant-id changes
            this.setupProductCardObserver();
        }

        setupEventListeners() {
            // Handle option selection
            this.optionInputs.forEach((input, index) => {
                input.addEventListener('change', this._onOptionChange);
            });
            
            // Handle quantity selection
            this.quantityInputs.forEach((input, index) => {
                input.addEventListener('change', this._onQtyChange);
                // Also listen for click events to handle cases where the input is already checked
                // (clicking a checked radio doesn't fire a change event)
                input.addEventListener('click', (e) => {
                    // Only handle if this is a user click and the input is (or will be) checked
                    if (e.isTrusted && input.hasAttribute('data-variant-id')) {
                        // Check if input is already checked (will be true for already-checked radios)
                        // or if it will be checked after this click (for unchecked radios)
                        const willBeChecked = input.checked || input.value === e.target.value;
                        
                        if (willBeChecked) {
                            // Mark this as a user-initiated change
                            input.dataset.userInitiated = 'true';
                            // Force a change event to ensure handleQuantityChange is called
                            // Use a small delay to ensure the click event completes first
                            setTimeout(() => {
                                const changeEvent = new Event('change', { bubbles: true });
                                // Try to mark as trusted (may not work in all browsers, but worth trying)
                                try {
                                    Object.defineProperty(changeEvent, 'isTrusted', { value: true, writable: false });
                                } catch (err) {
                                    // Some browsers don't allow modifying isTrusted
                                }
                                input.dispatchEvent(changeEvent);
                                // Clean up the flag
                                delete input.dataset.userInitiated;
                            }, 0);
                        }
                    }
                });
            });
        }

        handleOptionChange(event) {
            if (event.target.type === 'radio' && event.target.name.includes('-')) {
                // Skip if we're syncing from another instance to prevent cascades
                if (this._isSyncing) return;
                
                // MIRROR forms: Don't process locally, just request ORIGINAL to update
                // ORIGINAL will process and broadcast the correct state back
                if (this.mirrorSourceEl) {
                    const optionName = this.getOptionNameFromInput(event.target);
                    document.dispatchEvent(new CustomEvent('product-options:mirror-request', {
                        detail: {
                            productHandle: this.productHandle,
                            option: optionName,
                            value: event.target.value
                        }
                    }));
                    return;
                }
                
                // ORIGINAL: Process normally
                this.cacheCurrentTierState();
                this.updateVariantSelector();
                this.updateSelectedOptionDisplay(event.target);
                this.broadcastSelection();
            }
        }
        
        cacheCurrentTierState() {
            const { selectedQuantity, isRelatedVariant } = this.getCurrentTierState();
            // Only cache if we have a valid selection - never overwrite with null/false
            if (selectedQuantity !== null) {
                this.selectedQuantity = selectedQuantity;
                this._cachedIsRelatedVariant = isRelatedVariant;
                this._updateAlternateBodyClass();
            }
        }

        getCurrentTierState() {
            const selectedQuantityInput = this.querySelector('input[name="quantity"]:checked');
            if (selectedQuantityInput) {
                const relatedVariantId = parseInt(selectedQuantityInput.dataset.variantId);
                return {
                    selectedQuantity: parseInt(selectedQuantityInput.value),
                    isRelatedVariant: this.isRelatedVariant(relatedVariantId)
                };
            }
            return { selectedQuantity: null, isRelatedVariant: false };
        }

        handleQuantityChange(event) {
            // CRITICAL: Skip if we're inside updateQuantityPicker to prevent cascade
            if (this._isUpdatingQuantityPicker) return;

            // Quantity changes don't affect variant selection, just update display
            // Tailwind 4 :has() selector handles visual states automatically
            // Moved broadcastSelection to after update to ensure correct state is sent

            // Check if selected quantity tier has a related variant ID
            if (event.target && event.target.hasAttribute('data-variant-id')) {
                const relatedVariantId = parseInt(event.target.getAttribute('data-variant-id'));
                if (relatedVariantId && this.variantSelector) {

                    // Check if variant actually changed
                    const currentVariantId = parseInt(this.variantSelector.value || '0');
                    // Check if this is a real user click vs programmatic
                    // Also check for the userInitiated flag set by the click handler
                    const isUserClick = event.isTrusted === true || event.target?.dataset?.userInitiated === 'true';

                    // Update cached tier state - IMPORTANT for out-of-stock option display
                    const wasRelatedVariant = this._cachedIsRelatedVariant;
                    this._cachedIsRelatedVariant = this.isRelatedVariant(relatedVariantId);
                    this._updateAlternateBodyClass();
                    this.selectedQuantity = parseInt(event.target.value);

                    // CRITICAL FIX: For user clicks, ALWAYS proceed with update, even if IDs match
                    // This ensures that when a user explicitly clicks a quantity option, the variant
                    // is properly set and all listeners are notified, even if the variant ID appears
                    // to be the same (the state might be out of sync or the variant needs to be "refreshed")
                    // Only skip for programmatic changes where IDs match
                    if (currentVariantId === relatedVariantId && !isUserClick) {
                        // Variant hasn't changed, but tier type may have changed
                        // Update out-of-stock options if tier type changed
                        if (wasRelatedVariant !== this._cachedIsRelatedVariant) {
                            this.updateOutOfStockOptions();
                        }
                        return;
                    }

                    // Check if variant exists in selector
                    const variantOption = this.variantSelector.querySelector(`option[value="${relatedVariantId}"]`);

                    if (variantOption) {
                        // Update variant selector to related variant ID (silently)
                        this.variantSelector.value = relatedVariantId;
                        this.currentVariant = {
                            id: relatedVariantId,
                            available: !variantOption.disabled,
                            option: variantOption
                        };

                        // Update URL with new variant
                        this.updateUrlWithVariant(relatedVariantId);

                        // Update media blocks for the new variant
                        this.updateMediaBlocksForVariant(relatedVariantId);

                        // Dispatch event for other components
                        // If inside a product-card, dispatch on the product-card with no bubbling
                        // If on main page, dispatch on document
                        const eventTarget = this.scopeContainer || document;
                        eventTarget.dispatchEvent(new CustomEvent('product-options:variant-changed', {
                            bubbles: false,
                            detail: {
                                variantId: relatedVariantId,
                                variant: this.currentVariant
                            }
                        }));

                        // Also update product card media blocks
                        this.updateProductCardMediaBlocks();

                        // Update add to cart button state for the new variant
                        this.updateAddToCartButton(this.currentVariant);

                        // Update out-of-stock options based on selected tier (standard vs related)
                        this.updateOutOfStockOptions();

                        // Broadcast the NEW selection state
                        this.broadcastSelection();
                        return;
                    }
                }
            }

            // Fallback: use variant determined by product options
            if (this.variantSelector) {
                const selectedOptions = this.getSelectedOptions();
                const matchingVariant = this.findMatchingVariant(selectedOptions, this._cachedIsRelatedVariant || false);
                if (matchingVariant) {

                    // Check if variant actually changed
                    const currentVariantId = parseInt(this.variantSelector.value || '0');
                    if (currentVariantId === parseInt(matchingVariant.id)) {
                        // Variant hasn't changed, no need to update
                        return;
                    }

                    // Check if currently selected variant is related
                    if (this.isRelatedVariant(currentVariantId)) {
                        if (this.areRelatedVariants(currentVariantId, parseInt(matchingVariant.id))) {
                             return;
                        }
                    }

                    // Update cached tier state - switching to standard tier
                    this._cachedIsRelatedVariant = false;
                    this._updateAlternateBodyClass();

                    this.variantSelector.value = matchingVariant.id;
                    this.currentVariant = matchingVariant;

                    // Dispatch change event for components that need to update
                    this.variantSelector.dispatchEvent(new Event('change', { bubbles: true }));

                    // Update add to cart button state for the new variant
                    this.updateAddToCartButton(matchingVariant);

                    // Update out-of-stock options based on selected tier
                    this.updateOutOfStockOptions();

                    // Broadcast the NEW selection state
                    this.broadcastSelection();
                }
            }
        }

        updateVariantSelector() {
            if (!this.variantSelector) {
                return;
            }

            // Get all selected options
            const selectedOptions = this.getSelectedOptions();

            // Pass tier preference so the correct variant type (main vs related) is found first
            const matchingVariant = this.findMatchingVariant(selectedOptions, this._cachedIsRelatedVariant || false);

            if (matchingVariant) {
                
                this.variantSelector.value = matchingVariant.id;
                this.currentVariant = matchingVariant;
                
                // Trigger change event for other components
                // NOTE: handleVariantSelectorChange will run synchronously here, which calls
                // updateUrlWithVariant, updateAddToCartButton, and updateQuantityPicker.
                // updateQuantityPicker may change the active variant (e.g., to a related product tier).
                this.variantSelector.dispatchEvent(new Event('change', { bubbles: true }));

                // Check if handleVariantSelectorChange → updateQuantityPicker changed the variant
                const postChangeVariantId = parseInt(this.variantSelector.value, 10);
                const pickerChangedVariant = postChangeVariantId !== parseInt(matchingVariant.id, 10);

                if (pickerChangedVariant && this.currentVariant) {
                    // Quantity picker selected a different variant (e.g., related product tier).
                    // URL and media were already updated by updateQuantityPicker.
                    // Update ATC button for the actual current variant to get correct OOS/BIS state.
                    this.updateOutOfStockOptions();
                    this.updateAddToCartButton(this.currentVariant);
                } else {
                    // Normal flow - variant wasn't changed by picker
                    this.updateUrlWithVariant(matchingVariant.id);
                    this.updateQuantityPicker(parseInt(matchingVariant.id));
                    this.updateOutOfStockOptions();
                    this.updateAddToCartButton(matchingVariant);
                    this.updateMediaBlocks(matchingVariant);
                }
            }
        }
        
        handleVariantSelectorChange(event) {
            const variantId = parseInt(event.target.value);
            
            // CRITICAL: Skip if we're inside updateQuantityPicker to prevent cascade
            if (this._isUpdatingQuantityPicker) {
                return;
            }
            
            if (variantId) {
                // Store previous variant ID to check if we're switching between related variants
                const previousVariantId = this.currentVariant ? parseInt(this.currentVariant.id, 10) : null;
                
                // Update URL first when variant switches (correct handle for main vs related)
                this.updateUrlWithVariant(variantId);
                
                // Update current variant
                const selectedOption = event.target.querySelector('option:checked');
                if (selectedOption) {
                    this.currentVariant = {
                        id: variantId,
                        available: !selectedOption.disabled
                    };
                    
                    // Update selected options state (hidden options remain unchanged)
                    this.updateSelectedOptions();
                    
                    // Update out-of-stock indicators
                    this.updateOutOfStockOptions();
                    
                    // Update add to cart button state
                    this.updateAddToCartButton(this.currentVariant);
                }
                
                // Skip quantity picker re-render if switching between related variants
                // (they share the same tiers, re-rendering would cause reversion)
                const areRelated = previousVariantId ? this.areRelatedVariants(previousVariantId, variantId) : false;
                if (previousVariantId && areRelated) {
                    return;
                }
                
                this.updateQuantityPicker(variantId);
            }
        }
        
        getDiscountTiersForVariant(variantId) {
            // Normalize variant ID to number for comparison
            let normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
            let relatedTiers = [];
            let currentTiers = [];

            // If variant tiers exist and ignoreVariantsMetafields is false, use variant tiers
            if (this.variantTiers && Array.isArray(this.variantTiers) ) {
                const originalVariant = this.variantTiers.find(v => {
                    const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
                    return vId === normalizedVariantId;
                });
                if (originalVariant && originalVariant.original_variant_id) {
                    normalizedVariantId = originalVariant.original_variant_id;
                }

                const relatedVariantTiers = this.variantTiers.find(v => {
                    const vId = typeof v.original_variant_id === 'string' ? parseInt(v.original_variant_id, 10) : v.original_variant_id;
                    return vId === normalizedVariantId;
                });
                if (relatedVariantTiers) {
                    if (relatedVariantTiers.tiers && Array.isArray(relatedVariantTiers.tiers) && relatedVariantTiers.tiers.length > 0) {
                        relatedTiers = relatedVariantTiers.tiers.map(tier => ({
                            ...tier,
                            variant_id: relatedVariantTiers.id,
                            price: relatedVariantTiers.price,
                        }));
                    } else {
                        const relatedPackNumber = this.relatedProductPackNumber || 1;
                        relatedTiers = [
                            {
                                variant_id: relatedVariantTiers.id,
                                price: relatedVariantTiers.price,
                                title: `${relatedPackNumber} Pack`,
                                quantity: relatedPackNumber,
                                discount_amount_value: 0,
                                discount_amount_title: "",
                                heading_highlight: "",
                                has_info_button: false,
                                info_content: "",
                            }
                        ];
                    }
                }
                if (!this.ignoreVariantsMetafields) {
                    // Compare both as numbers to handle type mismatches
                    const variantData = this.variantTiers.find(v => {
                        const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
                        return vId === normalizedVariantId;
                    });

                    // If variant has tiers property, use it (even if empty array) - variant takes priority
                    if (variantData && variantData.tiers !== undefined) {
                        // Handle nested tiers structure: { tiers: { tiers: [...] } }
                        let currentProductTiers = [];
                        if (Array.isArray(variantData.tiers)) {
                            currentProductTiers = variantData.tiers;
                        } else if (variantData.tiers && variantData.tiers.tiers && Array.isArray(variantData.tiers.tiers)) {
                            currentProductTiers = variantData.tiers.tiers;
                        }

                        if (currentProductTiers.length === 0 && relatedTiers.length > 0 && variantData.price) {
                            const packNumber = this.productPackNumber || 1;
                            currentTiers = [
                                {
                                    variant_id: variantData.id,
                                    price: variantData.price,
                                    title: `${packNumber} Pack`,
                                    quantity: packNumber,
                                    discount_amount_value: 0,
                                    discount_amount_title: "",
                                    heading_highlight: "",
                                    has_info_button: false,
                                    info_content: "",
                                }
                            ];
                        } else {
                            currentTiers = currentProductTiers;
                        }
                    }
                }
            }

            // Pack number for ordering: use title first (e.g. "3 pack" → 3) since some metafields use quantity as 1,2,3 multiplier
            const tierPackNumber = (tier) => {
                if (tier.title) {
                    const m = tier.title.match(/(\d+)/);
                    if (m) return parseInt(m[1], 10);
                }
                const q = tier.quantity != null && tier.quantity !== '' ? parseInt(tier.quantity, 10) : NaN;
                if (isNaN(q)) return 1;

                // Variant tier quantities are multipliers of that product's
                // base pack. This keeps ordering correct even when a tier
                // metafield omits its display title.
                const tierVariantId = tier.variant_id ? parseInt(tier.variant_id, 10) : null;
                const packSize = tierVariantId && this.isRelatedVariant(tierVariantId)
                    ? (this.relatedProductPackNumber || 1)
                    : (this.productPackNumber || 1);
                return q * packSize;
            };

            // Merge order: put the group whose smallest pack (by title/display) is lower first
            const minCurrent = currentTiers.length ? Math.min(...currentTiers.map(tierPackNumber), 99) : 99;
            const minRelated = relatedTiers.length ? Math.min(...relatedTiers.map(tierPackNumber), 99) : 99;
            const currentFirst = minCurrent <= minRelated;
            let tiersArray = currentFirst
                ? [...currentTiers, ...relatedTiers]
                : [...relatedTiers, ...currentTiers];

            // When there is only one tier, filter out tiers which have empty title & discount_amount_value 0
            if (tiersArray.length === 1) {
                tiersArray = tiersArray.filter(tier => {
                    return tier.title != '' && parseFloat(tier.discount_amount_value, 10) > 0;
                });
            }

            // Sort by pack number (from title then quantity) so order is always 1, 3, 6, 9
            if (tiersArray.length > 1) {
                tiersArray.sort((a, b) => tierPackNumber(a) - tierPackNumber(b));
            }

            // Return tiers array (even if empty) - don't fall back to product
            return tiersArray || [];
        }
        
        getVariantPrice(variantId) {
            // Normalize variant ID to number for comparison
            const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
            
            if (this.variantTiers && Array.isArray(this.variantTiers)) {
                // Compare both as numbers to handle type mismatches
                const variantData = this.variantTiers.find(v => {
                    const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
                    return vId === normalizedVariantId;
                });
                if (variantData && variantData.price) {
                    // Price is already in cents from Liquid
                    return variantData.price;
                }
            }
            return null;
        }
        
        formatMoney(cents) {
            // Use global utility function from assets/utils.js
            const amount = cents / 100;
            return window.theme.utils.money(amount);
        }
        
        calculateDiscountedPrice(variantPrice, discountPercentage) {
            if (!variantPrice || !discountPercentage) {
                return null;
            }
            
            const discountDecimal = discountPercentage / 100.0;
            const discountAmount = variantPrice * discountDecimal;
            const discountedPrice = variantPrice - discountAmount;
            
            const result = window.theme?.utils?.roundPrice ? 
                window.theme.utils.roundPrice(discountedPrice, true) : 
                Math.round(discountedPrice); // Fallback to nearest cent
            
            return result;
        }
        
        
        isRelatedVariant(variantId) {
            if (!this.variantTiers || !Array.isArray(this.variantTiers)) {
                return false;
            }
            
            // Normalize variant ID to number
            const normalizedId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
            
            const variantData = this.variantTiers.find(v => {
                const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
                return vId === normalizedId;
            });
            
            return variantData && !!variantData.original_variant_id;
        }

        areRelatedVariants(variantId1, variantId2) {
            // Check if two variants are related (one is the original, the other is the alternate)
            if (!this.variantTiers || !Array.isArray(this.variantTiers)) {
                return false;
            }
            
            // Normalize both variant IDs to numbers
            const normalizedId1 = typeof variantId1 === 'string' ? parseInt(variantId1, 10) : variantId1;
            const normalizedId2 = typeof variantId2 === 'string' ? parseInt(variantId2, 10) : variantId2;
            
            // Find variant data for both IDs
            const variant1 = this.variantTiers.find(v => {
                const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
                return vId === normalizedId1;
            });
            
            
            const variant2 = this.variantTiers.find(v => {
                const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
                return vId === normalizedId2;
            });
            
            if (!variant1 || !variant2) {
                return false;
            }
            
            // Check if variant1 is the alternate of variant2's original
            if (variant1.original_variant_id) {
                const originalId1 = typeof variant1.original_variant_id === 'string' 
                    ? parseInt(variant1.original_variant_id, 10) 
                    : variant1.original_variant_id;
                if (originalId1 === normalizedId2) {
                    return true;
                }
            }
            
            // Check if variant2 is the alternate of variant1's original
            if (variant2.original_variant_id) {
                const originalId2 = typeof variant2.original_variant_id === 'string' 
                    ? parseInt(variant2.original_variant_id, 10) 
                    : variant2.original_variant_id;
                if (originalId2 === normalizedId1) {
                    return true;
                }
            }
            
            // Check if both variants share the same original_variant_id (both are alternates of the same original)
            if (variant1.original_variant_id && variant2.original_variant_id) {
                const originalId1 = typeof variant1.original_variant_id === 'string' 
                    ? parseInt(variant1.original_variant_id, 10) 
                    : variant1.original_variant_id;
                const originalId2 = typeof variant2.original_variant_id === 'string' 
                    ? parseInt(variant2.original_variant_id, 10) 
                    : variant2.original_variant_id;
                if (originalId1 === originalId2) {
                    return true;
                }
            }
            
            return false;
        }
        
        getOriginalVariantId(variantId) {
             const normalizedId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
             if (!this.variantTiers) return normalizedId;
             const v = this.variantTiers.find(t => (typeof t.id === 'string' ? parseInt(t.id,10) : t.id) === normalizedId);
             if (v && v.original_variant_id) {
                 return typeof v.original_variant_id === 'string' ? parseInt(v.original_variant_id, 10) : v.original_variant_id;
             }
             return normalizedId;
        }

        updateQuantityPicker(variantId) {
            // Prevent recursive calls
            if (this._isUpdatingQuantityPicker) {
                return;
            }

            const quantityContainer = this.querySelector('[data-quantity-options-container="true"]');
            if (!quantityContainer) return;

            // Normalize variant ID to number
            const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;

            // Check if variant actually changed
            const currentVariantId = parseInt(quantityContainer.dataset.variantId || '0');
            // Check if quantity picker is already rendered (has quantity inputs)
            const hasQuantityInputs = quantityContainer.querySelectorAll('input[name="quantity"]').length > 0;

            // Only skip update if variant hasn't changed AND quantity picker is already rendered
            if (currentVariantId === normalizedVariantId && hasQuantityInputs) return;
            
            // Set flag to prevent recursive calls
            this._isUpdatingQuantityPicker = true;
            
            try {
                // Determine the "Base" variant ID for rendering (Original)
                // This ensures Main tiers get the Original ID, and Alt tiers get the Alt ID.
                const renderVariantId = this.getOriginalVariantId(normalizedVariantId);

                // Use cached tier state from before variant change (set in cacheCurrentTierState)
                const previouslySelectedQuantity = this.selectedQuantity || null;
                let preferRelated = this._cachedIsRelatedVariant || false;
                
                // Get tiers for this variant
                const tiers = this.getDiscountTiersForVariant(normalizedVariantId);

                if (!tiers || tiers.length === 0) {
                    // If no tiers, hide the quantity picker
                    const pickerContainer = this.querySelector('[data-quantity-picker-container="true"]');
                    if (pickerContainer) {
                        pickerContainer.style.display = 'none';
                    }
                    return;
                }
                
                // Show the quantity picker if it was hidden
                const pickerContainer = this.querySelector('[data-quantity-picker-container="true"]');
                if (pickerContainer) {
                    pickerContainer.style.display = '';
                }
                
                // Get variant price for RENDERING (match renderVariantId)
                let renderPrice = this.getVariantPrice(renderVariantId);
                if (!renderPrice) {
                    // Fallback to passed variant price if primary fails (unlikely)
                    renderPrice = this.getVariantPrice(variantId);
                    
                     // Fallback: use current variant price from container
                    if (!renderPrice) {
                        const currentPrice = parseInt(quantityContainer.dataset.variantPrice || '0');
                        if (currentPrice) {
                            renderPrice = currentPrice;
                        }
                    }
                }
                
                if (!renderPrice) {
                    return;
                }
                
                // Update variant ID and price in container
                quantityContainer.dataset.variantId = variantId;
                quantityContainer.dataset.variantPrice = renderPrice;
                
                // Determine which quantity to select
                let quantityToSelect = 0;
                
                // 1. Try to maintain previous quantity selection
                if (previouslySelectedQuantity) {
                    quantityToSelect = previouslySelectedQuantity;
                } 
                // 2. Initial load: Match the passed variantId to a tier to determine default quantity
                else {
                    const matchingTier = tiers.find(t => {
                        const tierId = t.variant_id ? parseInt(t.variant_id) : renderVariantId;
                        // Match against the requested Variant ID (normalizedVariantId)
                        return tierId === normalizedVariantId;
                    });
                    
                    if (matchingTier) {
                        quantityToSelect = parseInt(matchingTier.quantity);
                        
                        const tierId = matchingTier.variant_id ? parseInt(matchingTier.variant_id) : renderVariantId;
                        if (this.isRelatedVariant(tierId)) {
                            preferRelated = true;
                        }
                    } else {
                         // Default to first tier
                         quantityToSelect = tiers[0] ? parseInt(tiers[0].quantity || '1') : 1;
                    }
                }
                
                // Get settings from container data attributes
                const showOriginalPrice = quantityContainer.dataset.showOriginalPrice !== 'false';
                const showDiscountBadge = quantityContainer.dataset.showDiscountBadge !== 'false';
                
                // Render the quantity picker HTML using renderVariantId and renderPrice
                const html = this.renderQuantityPicker(tiers, renderVariantId, renderPrice, quantityToSelect, showOriginalPrice, showDiscountBadge);
                quantityContainer.innerHTML = html;
                
                // Re-attach event listeners to the new inputs
                const newQuantityInputs = quantityContainer.querySelectorAll('input[name="quantity"]');
                newQuantityInputs.forEach(input => {
                    input.addEventListener('change', this._onQtyChange);
                    // Also listen for click events to handle cases where the input is already checked
                    // (clicking a checked radio doesn't fire a change event)
                    input.addEventListener('click', (e) => {
                        // Only handle if this is a user click and the input is (or will be) checked
                        if (e.isTrusted && input.hasAttribute('data-variant-id')) {
                            // Check if input is already checked (will be true for already-checked radios)
                            // or if it will be checked after this click (for unchecked radios)
                            const willBeChecked = input.checked || input.value === e.target.value;
                            
                            if (willBeChecked) {
                                // Mark this as a user-initiated change
                                input.dataset.userInitiated = 'true';
                                // Force a change event to ensure handleQuantityChange is called
                                // Use a small delay to ensure the click event completes first
                                setTimeout(() => {
                                    const changeEvent = new Event('change', { bubbles: true });
                                    // Try to mark as trusted (may not work in all browsers, but worth trying)
                                    try {
                                        Object.defineProperty(changeEvent, 'isTrusted', { value: true, writable: false });
                                    } catch (err) {
                                        // Some browsers don't allow modifying isTrusted
                                    }
                                    input.dispatchEvent(changeEvent);
                                    // Clean up the flag
                                    delete input.dataset.userInitiated;
                                }, 0);
                            }
                        }
                    });
                });
                
                // Update quantityInputs array
                this.quantityInputs = Array.from(newQuantityInputs);
                
                // Select specific tier based on preference (Original vs Related)
                let inputToSelect = null;
                
                // Try to find input that matches quantity AND type preference
                const inputs = Array.from(newQuantityInputs);
                inputToSelect = inputs.find(input => {
                     const val = parseInt(input.value);
                     if (val !== quantityToSelect) return false;
                     
                     const inputVId = parseInt(input.dataset.variantId || '0');
                     
                     // Use the helper method consistently
                     const isRelated = this.isRelatedVariant(inputVId);
                     return isRelated === preferRelated;
                });
                
                // Fallback: if exact type match not found, find any with matching quantity
                if (!inputToSelect) {
                    inputToSelect = quantityContainer.querySelector(`input[name="quantity"][value="${quantityToSelect}"]`);
                }

                if (inputToSelect) {
                    inputToSelect.checked = true;

                    // Directly update variant selector if tier has a different variant ID
                    // NO dispatch - just set the value and update state directly
                    const selectedInputVariantId = parseInt(inputToSelect.dataset.variantId || '0');
                    
                    if (selectedInputVariantId && this.variantSelector && selectedInputVariantId !== parseInt(this.variantSelector.value)) {
                        this.variantSelector.value = selectedInputVariantId;
                        
                        // Update current variant state
                        const variantOption = this.variantSelector.querySelector(`option[value="${selectedInputVariantId}"]`);
                        if (variantOption) {
                            this.currentVariant = {
                                id: selectedInputVariantId,
                                available: !variantOption.disabled,
                                option: variantOption
                            };
                        }
                        
                        // Update URL without dispatching events
                        this.updateUrlWithVariant(selectedInputVariantId);
                        
                        // Update media blocks for the selected variant
                        this.updateMediaBlocksForVariant(selectedInputVariantId);
                    }
                }
                
            } finally {
                // Always reset the flag
                this._isUpdatingQuantityPicker = false;
            }
        }
        
        renderQuantityPicker(tiers, variantId, variantPrice, selectedQuantity = null, showOriginalPrice = true, showDiscountBadge = true) {
            let html = '';
            
            // Default to first tier if no quantity specified
            if (!selectedQuantity && tiers.length > 0) {
                selectedQuantity = parseInt(tiers[0].quantity || '1');
            }
            
            tiers.forEach((tier, index) => {
                const quantity = parseInt(tier.quantity || '1');
                const discountPercentage = parseFloat(tier.discount_amount_value) || 0;
                const isSelected = selectedQuantity === quantity;

                let tierPrice = tier.price || variantPrice;

                // Each tier price is the price of one base pack. The quantity is
                // the number of base packs, including when the tier came from
                // the related product (e.g. 2 and 3 for the 6/9-pack tiers).
                const basePrice = tierPrice * quantity;

                const discountedPriceCents = discountPercentage > 0 
                    ? this.calculateDiscountedPrice(basePrice, discountPercentage)
                    : basePrice;
                
                // Quantity label
                const displayQty = quantity * this.quantityLabelMultiplier;
                let labelText = `${displayQty} ${this.quantityUnitLabel}`;
                if (tier.title) {
                    labelText = tier.title;
                }
                
                // Build price HTML
                let priceHtml = '';
                if (discountPercentage > 0 && basePrice !== discountedPriceCents) {
                    priceHtml = `
                        <span class="font-medium text-sm leading-tight text-black">
                            ${this.formatMoney(discountedPriceCents)}
                        </span>
                        ${showOriginalPrice ? `
                        <span class="font-normal text-sm leading-tight text-[#454545] line-through opacity-75">
                            ${this.formatMoney(basePrice)}
                        </span>
                        ` : ''}
                    `;
                } else {
                    priceHtml = `
                        <span class="font-medium text-sm leading-tight text-black">
                            ${this.formatMoney(basePrice)}
                        </span>
                    `;
                }
                
                // Build badge HTML
                let badgeHtml = '';
                if (showDiscountBadge && discountPercentage > 0) {
                    badgeHtml = `
                        <div class="bg-[#C90016] px-2 py-0.5 rounded-full shrink-0 flex">
                            <span class="font-medium text-xs leading-[1.1] text-center text-white">
                                SAVE ${discountPercentage}%
                            </span>
                        </div>
                    `;
                }
                
                // Build label HTML
                html += `
                    <label class="group flex items-center justify-between px-3 py-2 h-10 rounded-full border border-gray-300 bg-white cursor-pointer transition-all duration-200 has-[:checked]:bg-gray-100 has-[:checked]:border-black" 
                           data-quantity="${quantity}">
                        <div class="flex gap-2 h-full items-center shrink grow">
                            <div class="flex gap-2 h-full items-center">
                                <input 
                                    type="radio" 
                                    name="quantity" 
                                    value="${quantity}" 
                                    id="quantity-${quantity}${tier.variant_id ? `-${tier.variant_id}` : ''}"
                                    ${isSelected ? 'checked' : ''}
                                    class="sr-only"
                                    style="display: none;"
                                    data-variant-id="${tier.variant_id ? tier.variant_id : variantId}"
                                >
                                <div class="w-2.5 h-2.5 rounded-full border border-black bg-white group-has-[:checked]:border-2 shrink-0"></div>
                                <div class="font-medium text-sm leading-tight text-black cursor-pointer">
                                    ${labelText}
                                </div>
                            </div>
                        </div>
                        
                        <div class="flex gap-2 items-center shrink-0">
                            <div class="flex gap-1 items-center">
                                ${priceHtml}
                            </div>
                            ${badgeHtml}
                        </div>
                    </label>
                `;
            });
            
            return html;
        }

        getSelectedOptions() {
            const selectedOptions = {};
            
            this.optionInputs.forEach(input => {
                if (input.checked) {
                    const optionName = this.getOptionNameFromInput(input);
                    selectedOptions[optionName] = input.value;
                }
            });
            
            return selectedOptions;
        }

        getOptionNameFromInput(input) {
            // Extract option name from input name (format: "OptionName-blockId-productId")
            const nameParts = input.name.split('-');
            return nameParts[0];
        }

        findMatchingVariant(selectedOptions, preferRelated = false) {
            if (!this.variantSelector) return null;

            // Order variants so the preferred type (main vs related) is searched first.
            // This prevents the related product's variant from being returned when both
            // share the same option values (e.g. Color=Black / Size=XL) and the user
            // intends to stay on the main product's tier.
            const allVariants = Array.from(this.variantSelector.options);
            const orderedVariants = preferRelated
                ? [
                    ...allVariants.filter(opt => this.isRelatedVariant(parseInt(opt.value))),
                    ...allVariants.filter(opt => !this.isRelatedVariant(parseInt(opt.value)))
                  ]
                : [
                    ...allVariants.filter(opt => !this.isRelatedVariant(parseInt(opt.value))),
                    ...allVariants.filter(opt => this.isRelatedVariant(parseInt(opt.value)))
                  ];

            // Find variant that matches all selected options
            const matchingVariant = orderedVariants.find(option => {
                // First, try data attributes if they're populated
                let hasDataAttributes = false;
                let matches = true;
                
                for (const [optionName, optionValue] of Object.entries(selectedOptions)) {
                    // Get data attribute (option names are lowercase with underscores)
                    // Match the Liquid format: downcase and replace spaces with underscores
                    const dataAttr = optionName.toLowerCase().replace(/\s+/g, '_');
                    
                    // Access dataset property (camelCase in JavaScript)
                    const camelCaseAttr = dataAttr.split('_').map((word, index) => {
                        if (index === 0) return word;
                        return word.charAt(0).toUpperCase() + word.slice(1);
                    }).join('');
                    
                    // Try both formats
                    const variantValue = option.dataset[camelCaseAttr] || option.getAttribute(`data-${dataAttr}`);
                    
                    if (variantValue && variantValue !== '') {
                        hasDataAttributes = true;
                        if (variantValue !== optionValue) {
                            matches = false;
                            break;
                        }
                    }
                }
                
                // If data attributes are populated and match, return this variant
                if (hasDataAttributes && matches) {
                    return true;
                }
                
                // Fallback: match by parsing variant title (format: "Option1 / Option2 / Option3")
                if (!hasDataAttributes || !matches) {
                    const variantTitle = option.text.trim();
                    const selectedValues = Object.values(selectedOptions);
                    
                    // Check if all selected option values appear in the variant title
                    // Split by " / " to get individual option values from variant title
                    const variantParts = variantTitle.split(' / ').map(part => part.trim());
                    
                    // Check if all selected values match variant parts (in any order)
                    const allMatch = selectedValues.every(value => {
                        return variantParts.some(part => part === value);
                    });
                    
                    // Also ensure we have the same number of matches (avoid partial matches)
                    if (allMatch && variantParts.length === selectedValues.length) {
                        return true;
                    }
                }
                
                return false;
            });
            
            if (matchingVariant) {
                // Check availability: check inventory data attributes
                let isAvailable = true;
                
                if (matchingVariant.dataset.inventoryPolicy && matchingVariant.dataset.maxQuantity !== undefined) {
                    const inventoryPolicy = matchingVariant.dataset.inventoryPolicy;
                    const maxQuantity = parseInt(matchingVariant.dataset.maxQuantity, 10);
                    if (inventoryPolicy === 'deny' && maxQuantity <= 0) {
                        isAvailable = false;
                    }
                }
                
                return {
                    id: matchingVariant.value,
                    available: isAvailable,
                    option: matchingVariant
                };
            }
            
            return null;
        }

        getProductVariants() {
            // Get all variants from the selector
            if (!this.variantSelector) return [];
            
            return Array.from(this.variantSelector.options).map(opt => {
                // Check availability based on inventory data attributes
                let isAvailable = true;
                if (opt.dataset.inventoryPolicy && opt.dataset.maxQuantity !== undefined) {
                    const inventoryPolicy = opt.dataset.inventoryPolicy;
                    const maxQuantity = parseInt(opt.dataset.maxQuantity, 10);
                    if (inventoryPolicy === 'deny' && maxQuantity <= 0) {
                        isAvailable = false;
                    }
                }
                return {
                id: opt.value,
                text: opt.text,
                    available: isAvailable,
                dataset: {...opt.dataset}
                };
            });
        }
        
        checkVariantAvailability(selectedOptions) {
            // Check if a combination of options would result in an available variant
            const variant = this.findMatchingVariant(selectedOptions);
            return variant ? variant.available : false;
        }

        /**
         * Check if a combination of options would result in an available RELATED variant.
         * This is used to show out-of-stock indicators for related product options.
         * @param {Object} selectedOptions - The selected option values
         * @returns {boolean} - True if the related variant is available, false otherwise
         */
        checkRelatedVariantAvailability(selectedOptions) {
            // Check if a combination of options would result in an available related variant
            const relatedVariant = this.findMatchingRelatedVariant(selectedOptions);
            return relatedVariant ? relatedVariant.available : false;
        }

        /**
         * Find a matching RELATED variant based on selected options.
         * Related variants have data-original-variant-id attribute set.
         * @param {Object} selectedOptions - The selected option values
         * @returns {Object|null} - The matching related variant or null
         */
        findMatchingRelatedVariant(selectedOptions) {
            if (!this.variantSelector) return null;

            // Get all related variants from the selector (those with original-variant-id)
            const allOptions = Array.from(this.variantSelector.options);
            const relatedVariants = allOptions.filter(option => {
                return option.dataset.originalVariantId;
            });

            if (relatedVariants.length === 0) return null;

            // Find related variant that matches all selected options
            const matchingVariant = relatedVariants.find(option => {
                // First, try data attributes if they're populated
                let hasDataAttributes = false;
                let matches = true;

                for (const [optionName, optionValue] of Object.entries(selectedOptions)) {
                    // Get data attribute (option names are lowercase with underscores)
                    const dataAttr = optionName.toLowerCase().replace(/\s+/g, '_');

                    // Access dataset property (camelCase in JavaScript)
                    const camelCaseAttr = dataAttr.split('_').map((word, index) => {
                        if (index === 0) return word;
                        return word.charAt(0).toUpperCase() + word.slice(1);
                    }).join('');

                    // Try both formats
                    const variantValue = option.dataset[camelCaseAttr] || option.getAttribute(`data-${dataAttr}`);

                    if (variantValue && variantValue !== '') {
                        hasDataAttributes = true;
                        if (variantValue !== optionValue) {
                            matches = false;
                            break;
                        }
                    }
                }

                // If data attributes are populated and match, return this variant
                if (hasDataAttributes && matches) {
                    return true;
                }

                // Fallback: match by parsing variant title (format: "Option1 / Option2 / Option3")
                if (!hasDataAttributes || !matches) {
                    const variantTitle = option.text.trim();
                    const selectedValues = Object.values(selectedOptions);

                    // Split by " / " to get individual option values from variant title
                    const variantParts = variantTitle.split(' / ').map(part => part.trim());

                    // Check if all selected values match variant parts (in any order)
                    const allMatch = selectedValues.every(value => {
                        return variantParts.some(part => part === value);
                    });

                    // Also ensure we have the same number of matches
                    if (allMatch && variantParts.length === selectedValues.length) {
                        return true;
                    }
                }

                return false;
            });

            if (matchingVariant) {
                // Check availability: check inventory data attributes
                let isAvailable = true;

                if (matchingVariant.dataset.inventoryPolicy && matchingVariant.dataset.maxQuantity !== undefined) {
                    const inventoryPolicy = matchingVariant.dataset.inventoryPolicy;
                    const maxQuantity = parseInt(matchingVariant.dataset.maxQuantity, 10);
                    if (inventoryPolicy === 'deny' && maxQuantity <= 0) {
                        isAvailable = false;
                    }
                }

                return {
                    id: matchingVariant.value,
                    available: isAvailable,
                    option: matchingVariant,
                    originalVariantId: matchingVariant.dataset.originalVariantId
                };
            }

            return null;
        }
        
        updateOutOfStockOptions() {
            // Get currently selected options
            const selectedOptions = this.getSelectedOptions();

            // Determine if we're currently viewing a related product tier
            // This affects which stock state we should display
            const isRelatedTierSelected = this._cachedIsRelatedVariant || false;

            // Check if there are related variants at all
            const hasRelatedVariants = this.hasRelatedVariants();

            // Check if this instance should apply pointer-events-none to unavailable options
            const disableUnavailable = this.dataset.disableUnavailable === 'true';

            // Get all option groups
            const optionGroups = this.getOptionGroups();

            // For each option group
            optionGroups.forEach((group) => {
                const optionName = group.name;

                // For each option in this group
                group.inputs.forEach(input => {
                    const label = input.closest('label');
                    if (!label) return;

                    const isCurrentlySelected = input.checked;

                    // Create a test selection with this option value
                    const testOptions = {...selectedOptions};
                    testOptions[optionName] = input.value;

                    // Check standard variant availability
                    const isStandardAvailable = this.checkVariantAvailability(testOptions);

                    // Check related variant availability (if related variants exist)
                    let isRelatedAvailable = true;
                    if (hasRelatedVariants) {
                        isRelatedAvailable = this.checkRelatedVariantAvailability(testOptions);
                    }

                    // Determine which availability to use based on selected tier
                    // If a related tier is selected, show related variant stock state
                    // Otherwise, show standard variant stock state
                    let isAvailable;
                    if (isRelatedTierSelected && hasRelatedVariants) {
                        isAvailable = isRelatedAvailable;
                    } else {
                        isAvailable = isStandardAvailable;
                    }

                    // Apply visual indicators based on availability
                    // Remove existing related product indicator
                    const existingRelatedIndicator = label.querySelector('.related-stock-indicator');
                    if (existingRelatedIndicator) {
                        existingRelatedIndicator.remove();
                    }

                    // Apply opacity-25 if not available (but allow selection)
                    // Strike-through is applied to child span to avoid conflict with label's :checked pseudo-elements
                    const contentSpan = label.querySelector('span');
                    const strikeClasses = ['after:content-[\'\']', 'after:absolute', 'after:top-0', 'after:bottom-0', 'after:left-1/2', 'after:-translate-x-1/2', 'after:origin-center', 'after:rotate-45', 'after:border-l', 'after:border-black', 'after:z-50'];

                    if (!isAvailable) {
                        if (isCurrentlySelected) {
                            label.classList.remove('opacity-25');
                        } else {
                            label.classList.add('opacity-25');
                        }
                        // Add strike-through to child span to preserve label's :checked pseudo-elements
                        if (contentSpan) {
                            contentSpan.classList.add(...strikeClasses);
                        }
                        // Block direct clicks but allow programmatic selection via linked forms
                        if (disableUnavailable) {
                            label.classList.add('pointer-events-none');
                        }
                    } else {
                        label.classList.remove('opacity-25');
                        if (contentSpan) {
                            contentSpan.classList.remove(...strikeClasses);
                        }
                        if (disableUnavailable) {
                            label.classList.remove('pointer-events-none');
                        }
                    }

                    // Add secondary indicator if there's a difference between standard and related availability
                    // This helps users understand that switching pack sizes may affect availability
                    // if (hasRelatedVariants && isStandardAvailable !== isRelatedAvailable && !isCurrentlySelected) {
                    //     // Show a subtle indicator that the "other" tier has different availability
                    //     const indicatorDot = document.createElement('span');
                    //     indicatorDot.className = 'related-stock-indicator absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full z-30';

                    //     if (isRelatedTierSelected) {
                    //         // Currently on related tier, standard is different
                    //         indicatorDot.classList.add(isStandardAvailable ? 'bg-green-500' : 'bg-red-500');
                    //         indicatorDot.title = isStandardAvailable
                    //             ? 'Available in standard pack'
                    //             : 'Also unavailable in standard pack';
                    //     } else {
                    //         // Currently on standard tier, related is different
                    //         indicatorDot.classList.add(isRelatedAvailable ? 'bg-green-500' : 'bg-red-500');
                    //         indicatorDot.title = isRelatedAvailable
                    //             ? 'Available in multi-pack'
                    //             : 'Also unavailable in multi-pack';
                    //     }

                    //     label.appendChild(indicatorDot);
                    // }
                });
            });
        }

        /**
         * Check if there are any related variants in the variant selector.
         * @returns {boolean} - True if related variants exist, false otherwise
         */
        hasRelatedVariants() {
            if (!this.variantSelector) return false;
            const relatedOptions = this.variantSelector.querySelectorAll('option[data-original-variant-id]');
            return relatedOptions.length > 0;
        }
        
        getOptionGroups() {
            // Group inputs by option name
            const groups = {};
            
            this.optionInputs.forEach(input => {
                const optionName = this.getOptionNameFromInput(input);
                if (!groups[optionName]) {
                    groups[optionName] = {
                        name: optionName,
                        inputs: []
                    };
                }
                groups[optionName].inputs.push(input);
            });
            
            return Object.values(groups);
        }
        
        updateAddToCartButton(variant) {
            // Find the form to get its ID
            const form = this.closest('form[action*="/cart/add"]');
            // Get form ID from form element, or from data attribute, or default to 'main-product-form'
            let formId = form ? form.id : null;
            if (!formId) {
                // Try to get from data attribute on product-options element
                formId = this.dataset.formId || 'main-product-form';
            }
            
            // Find the add to cart button by for attribute matching form ID
            let button = null;
            
            if (formId) {
                button = document.querySelector(`button[for="${formId}"], button[data-submit-product-form="${formId}"]`);
            }
            
            // If not found by for attribute, try to find in the form
            if (!button && form) {
                button = form.querySelector('button[type="submit"]');
            }
            
            // If still not found, try to find by traversing up the DOM tree
            if (!button) {
                // Loop through parent elements until one has a child button type submit element
                let currentElement = this.parentElement;
                while (currentElement && currentElement !== document.body) {
                    const submitButton = currentElement.querySelector('button[type="submit"]');
                    if (submitButton) {
                        button = submitButton;
                        break;
                    }
                    // Move up to the next parent
                    currentElement = currentElement.parentElement;
                }
            }
            
            // If still not found, try to find by text content
            if (!button) {
                const allButtons = document.querySelectorAll('button');
                button = Array.from(allButtons).find(btn => {
                    const text = btn.textContent.trim().toLowerCase();
                    return text.includes('add to cart') || text.includes('add') && text.includes('cart');
                });
            }
            
            if (!button) return;
            
            // Get the variant option from the variant selector to check inventory policy
            // CRITICAL: Look up by variant.id to avoid stale option:checked after updateQuantityPicker
            // changes the selector value to a different variant (e.g., related product tier)
            let inventoryPolicy = null;
            let maxQuantity = null;
            let selectedOption = null;
            if (this.variantSelector) {
                const variantIdForLookup = variant && variant.id != null ? String(variant.id) : null;
                selectedOption = (variantIdForLookup && this.variantSelector.querySelector(`option[value="${variantIdForLookup}"]`))
                    || this.variantSelector.querySelector('option:checked');
                if (selectedOption) {
                    // Use getAttribute to ensure we get the raw string value (handles "0" correctly)
                    const maxQuantityAttr = selectedOption.getAttribute('data-max-quantity') || selectedOption.dataset.maxQuantity;
                    const inventoryPolicyAttr = selectedOption.getAttribute('data-inventory-policy') || selectedOption.dataset.inventoryPolicy || null;
                    
                    inventoryPolicy = inventoryPolicyAttr;
                    if (maxQuantityAttr !== null && maxQuantityAttr !== undefined) {
                        maxQuantity = parseInt(maxQuantityAttr, 10);
                        if (isNaN(maxQuantity)) {
                            maxQuantity = null;
                        }
                    }
                }
            }
            
            // CRITICAL: Determine out of stock status from the selected option's inventory data
            // This ensures we check the actual DOM element's data, not the variant object
            const isOutOfStock = maxQuantity !== null && maxQuantity <= 0;
            const deniesBackorders = inventoryPolicy === 'deny';
            const shouldDisable = isOutOfStock && deniesBackorders;

            if (shouldDisable) {
                button.setAttribute('data-out-of-stock', 'true');
            } else {
                button.removeAttribute('data-out-of-stock');
            }
        }

        getCurrentVariant() {
            if (this.variantSelector && this.variantSelector.value) {
                const selectedOption = this.variantSelector.querySelector('option:checked') || 
                                     this.variantSelector.querySelector(`option[value="${this.variantSelector.value}"]`);
                if (selectedOption) {
                    return {
                        id: parseInt(this.variantSelector.value, 10),
                        available: !selectedOption.disabled,
                        option: selectedOption
                    };
                }
            }
            return null;
        }

        updateSelectedOptions() {
            // Initialize selected options based on current state
            this.optionInputs.forEach(input => {
                if (input.checked) {
                    const optionName = this.getOptionNameFromInput(input);
                    this.selectedOptions[optionName] = input.value;
                }
            });
        }

        updateSelectedOptionDisplay(selectedInput) {
            const selectedValue = selectedInput.value;
            
            // Get the option position from the input's data-input-id
            const inputId = selectedInput.dataset.inputId;
            if (!inputId) return;
            
            const optionPosition = inputId.split('-')[0];
            
            // Find the element with data-selected-value matching the option position
            const selectedValueElement = this.querySelector(`[data-selected-value="${optionPosition}"]`);
            if (selectedValueElement) {
                // Only check for custom text if the attribute is explicitly set
                const hasCustomText = selectedValueElement.dataset.hasCustomText === 'true';
                if (!hasCustomText) {
                    selectedValueElement.textContent = selectedValue.replace('Collection', '');
                }
            }
        }

        // --- Cross-instance sync ---
        broadcastSelection() {
            if (this._isSyncing) return;
            
            // Get form ID to include in broadcast for context matching
            const form = this.closest('form[action*="/cart/add"]');
            let formId = form ? form.id : null;
            if (!formId) {
                formId = this.dataset.formId || null;
            }
            
            const detail = {
                productHandle: this.productHandle,
                sourceId: this.id || null,
                sourceFormId: formId, // Include form ID for context matching
                options: this.getSelectedOptions(),
                quantity: this.getSelectedQuantity(),
                variantId: this.variantSelector ? this.variantSelector.value : null,
                // Include tier state so receivers can preserve it
                cachedIsRelatedVariant: this._cachedIsRelatedVariant,
                selectedQuantity: this.selectedQuantity
            };
            // If inside a product-card, dispatch on the product-card with no bubbling
            // If on main page, dispatch on document
            const eventTarget = this.scopeContainer || document;
            eventTarget.dispatchEvent(new CustomEvent('product-options:changed', { bubbles: false, detail }));
        }

        // Handle requests from MIRROR forms to update an option
        // ORIGINAL processes the change and broadcasts the result
        handleMirrorRequest(event) {
            const { productHandle, option, value } = event.detail || {};
            
            // Only ORIGINAL forms handle mirror requests
            if (this.mirrorSourceEl) return;
            if (!productHandle || productHandle !== this.productHandle) return;
            
            // CRITICAL: Only allow the MAIN product form instance to handle mirror requests
            // This prevents multiple instances from processing the same request and losing state
            // The main form is identified by: having id="product-page-options" OR being inside main-product-form
            const isMainForm = this.id === 'product-page-options' || 
                              (this.closest('#main-product-form') !== null);
            
            // If this is not the main form, check if there's already a main form that should handle it
            if (!isMainForm) {
                const mainForm = document.getElementById('product-page-options') || 
                                document.querySelector('#main-product-form product-options');
                if (mainForm && mainForm !== this) {
                    // Main form exists and is different - let it handle the request instead
                    return;
                }
            }
            
            // Find and update the option input
            const target = this.querySelector(`input[type="radio"][name^="${CSS.escape(option)}-"][value="${CSS.escape(value)}"]`);
            if (target && !target.checked && !target.disabled) {
                // CRITICAL: Preserve existing tier state BEFORE making changes
                // Don't call cacheCurrentTierState() here because it may return null if quantity
                // input isn't checked at this moment, which would overwrite our cached value.
                // Instead, only update if we have a valid NEW selection.
                const { selectedQuantity: newSelectedQuantity, isRelatedVariant: newIsRelatedVariant } = this.getCurrentTierState();
                if (newSelectedQuantity !== null) {
                    // Only update if we have a valid new selection
                    this.selectedQuantity = newSelectedQuantity;
                    this._cachedIsRelatedVariant = newIsRelatedVariant;
                }
                // Otherwise, preserve existing cached values (this.selectedQuantity and this._cachedIsRelatedVariant remain unchanged)
                
                // Update the option
                target.checked = true;
                
                // Process the change (this will also broadcast)
                this.updateVariantSelector();
                this.updateSelectedOptionDisplay(target);
                this.broadcastSelection();
            }
        }

        onExternalSelection(event) {
            const { productHandle, sourceId, sourceFormId, options, quantity, variantId, cachedIsRelatedVariant, selectedQuantity } = event.detail || {};
            
            if (!productHandle || productHandle !== this.productHandle) return;
            // Ignore if this instance was the source
            if (sourceId && this.id && sourceId === this.id) return;
            
            // CRITICAL: Only sync if instances are explicitly linked OR share the same form context
            // This prevents recommendations/cart drawer forms from syncing with main product forms
            const form = this.closest('form[action*="/cart/add"]');
            let thisFormId = form ? form.id : null;
            if (!thisFormId) {
                thisFormId = this.dataset.formId || null;
            }
            
            // Check if explicitly linked via mirror_source
            const isExplicitlyLinked = (this.mirrorSourceEl && this.mirrorSourceEl.id === sourceId) ||
                                      (sourceId && document.getElementById(sourceId) === this.mirrorSourceEl);
            
            // Check if they share the same form ID (same form context)
            const shareSameForm = sourceFormId && thisFormId && sourceFormId === thisFormId;
            
            // Only sync if explicitly linked OR sharing same form context
            // This prevents cross-context syncing (e.g., cart drawer recommendations affecting main page)
            if (!isExplicitlyLinked && !shareSameForm) {
                return;
            }

            // Apply without causing loops
            this._isSyncing = true;
            try {
                // Preserve tier state from source if provided (critical for tier persistence)
                if (cachedIsRelatedVariant !== undefined) {
                    this._cachedIsRelatedVariant = cachedIsRelatedVariant;
                }
                if (selectedQuantity !== undefined) {
                    this.selectedQuantity = selectedQuantity;
                }
                
                // Sync options (only visible options - never change hidden options)
                Object.entries(options || {}).forEach(([optionName, value]) => {
                    const target = this.querySelector(`input[type="radio"][name^="${CSS.escape(optionName)}-"][value="${CSS.escape(value)}"]`);
                    if (target && !target.checked && !target.disabled) {
                        // Check if this input is in a hidden container - if so, skip it
                        const container = target.closest('.hidden');
                        if (container) {
                            // Skip hidden options - they should never be changed
                            return;
                        }
                        
                        target.checked = true;
                        target.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
                // Sync quantity
                if (typeof quantity === 'number') {
                    // Try to find input matching quantity AND variant ID (if provided)
                    let targetQty = null;
                    
                    if (variantId) {
                         targetQty = this.querySelector(`input[name="quantity"][value="${CSS.escape(String(quantity))}"][data-variant-id="${CSS.escape(variantId)}"]`);
                    }
                    
                    // Fallback to just quantity if specific variant match fails
                    if (!targetQty) {
                         targetQty = this.querySelector(`input[name="quantity"][value="${CSS.escape(String(quantity))}"]`);
                    }

                    if (targetQty && !targetQty.checked && !targetQty.disabled) {
                        targetQty.checked = true;
                        targetQty.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }
                
                // Update selected options state after syncing
                this.updateSelectedOptions();
                
                // Update out-of-stock indicators for this instance after syncing
                this.updateOutOfStockOptions();
            } finally {
                this._isSyncing = false;
            }
        }

        // --- URL update ---
        /**
         * Returns the product handle for the given variant (main or related).
         * Uses the variant selector option first (data-original-variant-id = related variant),
         * then falls back to variantTiers so URL updates correctly when switching to related product.
         */
        getHandleForVariant(variantId) {
            if (!this.variantSelector || !this.relatedProductHandle) {
                return this.isRelatedVariant(variantId) ? this.relatedProductHandle : (this.productHandle || '');
            }
            const normalizedId = variantId != null ? String(variantId) : '';
            const option = this.variantSelector.querySelector(`option[value="${normalizedId}"]`);
            if (option && (option.getAttribute('data-original-variant-id') != null || option.dataset.originalVariantId != null)) {
                return this.relatedProductHandle;
            }
            return this.productHandle || '';
        }

        /**
         * Updates URL only when variant switches. Uses correct product handle (main vs related).
         * Order: variant switch → check variant product → if handle different or variant different → update URL (handle + variant id).
         */
        updateUrlWithVariant(variantId) {
            if (!variantId || typeof window === 'undefined' || !window.history || !window.location) return;
            // Only update the URL for the main product page, not product cards or other scoped contexts
            if (this.scopeContainer) return;
            const handle = this.getHandleForVariant(variantId);
            if (!handle) return;

            const desiredPath = `/products/${handle}`;

            try {
                const url = new URL(window.location.href);
                const currentPath = url.pathname;
                const currentVariantParam = url.searchParams.get('variant');
                const newVariantId = String(variantId);

                // Only update when variant or product handle actually changed
                const pathChanged = currentPath !== desiredPath;
                const variantChanged = currentVariantParam !== newVariantId;
                if (!pathChanged && !variantChanged) return;

                url.pathname = desiredPath;
                url.searchParams.set('variant', newVariantId);

                if (window.theme?.utils?.dispatchUrlChange) {
                    window.theme.utils.dispatchUrlChange('product-variant', {
                        url: url.toString(),
                        variantId: newVariantId,
                        historyAction: 'replace'
                    });
                } else {
                    const originalReplaceState = window.theme?.utils?._originalReplaceState || history.replaceState;
                    originalReplaceState.call(history, {}, '', url.toString());
                }
            } catch (e) {
                console.error('Error updating URL with variant:', e);
            }
        }

        findRelatedVariantInTiers(originalVariantId) {
            const tiers = this.getDiscountTiersForVariant(originalVariantId);
            if (!tiers || !Array.isArray(tiers)) return null;
            // Find the tier which has a 'variant_id' attribute, and return that value, no comparison, just if it exists
            const relatedVariant = tiers.find(v => v.variant_id);
            return relatedVariant ? relatedVariant.variant_id : null;
        }

        updateMediaBlocks(variant) {
            let variantId = variant.id;
            if (this._cachedIsRelatedVariant) {
                const relatedId = this.findRelatedVariantInTiers(parseInt(variant.id));
                if (relatedId) {
                    variantId = relatedId;
                }
            }
            // Update images for product-options context (current product page)
            this.updateMediaBlocksForVariant(variantId);
            
            // Also update all product-card images based on their data-variant-id
            this.updateProductCardMediaBlocks();
            
            // Dispatch event with the CALCULATED variant ID (may be related variant)
            // If inside a product-card, dispatch on the product-card with no bubbling
            // If on main page, dispatch on document
            const eventTarget = this.scopeContainer || document;
            eventTarget.dispatchEvent(new CustomEvent('product-options:variant-changed', {
                bubbles: false,  // No propagation
                detail: {
                    variantId: variantId,
                    variant: { ...variant, id: variantId }
                }
            }));
        }
        
        updateMediaBlocksForVariant(variantId) {
            // Find all image wrappers (div/a) with data-variant-ids attribute
            // Only update those that are NOT within a product-card (product-card images are handled separately)
            // SCOPED: Only update elements within the same context (drawer vs main page)
            const imageWrappers = this.queryScopedElements('[data-variant-ids]');
            const variantIdStr = String(variantId);

            imageWrappers.forEach(wrapper => {
                // Skip if this wrapper is within a product-card (handled separately)
                if (wrapper.closest('product-card')) {
                    return;
                }

                this.updateImageWrapperVisibility(wrapper, variantIdStr);
            });

            // Reinitialize any Embla carousels that contain these images
            this.reinitializeAffectedCarousels(imageWrappers);
        }

        updateProductCardMediaBlocks() {
            // Find all product-card elements with data-variant-id (excluding static cards)
            // SCOPED: Only update product-cards within the same context (drawer vs main page)
            const productCards = this.queryScopedElements('product-card[data-variant-id]:not([data-static])');
            productCards.forEach(productCard => {
                const productCardVariantId = productCard.getAttribute('data-variant-id');
                if (!productCardVariantId) return;
                
                // Find all image wrappers within this product card
                const imageWrappers = productCard.querySelectorAll('[data-variant-ids]');
                
                imageWrappers.forEach(wrapper => {
                    this.updateImageWrapperVisibility(wrapper, String(productCardVariantId));
                });
                
                // Reinitialize any Embla carousels within this product card
                const carousels = productCard.querySelectorAll('embla-carousel');
                carousels.forEach(carousel => {
                    if (carousel.emblaApi && typeof carousel.handleBreakpointChange === 'function') {
                        // Use double RAF to ensure CSS class changes (hidden) are fully applied
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                carousel.handleBreakpointChange();
                            });
                        });
                    }
                });
            });
        }
        
        updateImageWrapperVisibility(wrapper, variantIdStr) {
            // Get variant IDs from data attribute (comma-separated string)
            const variantIdsAttr = wrapper.getAttribute('data-variant-ids');
            if (!variantIdsAttr) {
                // No variant IDs specified - show always (fallback images)
                wrapper.classList.remove('hidden');
                // Ensure embla__slide class is added if it's in a carousel
                if (wrapper.closest('embla-carousel')) {
                    wrapper.classList.add('embla__slide');
                }
                // Remove any inline display styles that might hide it
                const currentStyle = wrapper.getAttribute('style') || '';
                const cleanedStyle = currentStyle.replace(/display\s*:\s*none[^;]*;?/gi, '').trim();
                if (cleanedStyle) {
                    wrapper.setAttribute('style', cleanedStyle);
                } else {
                    wrapper.removeAttribute('style');
                }
                return;
            }
            
            // Parse variant IDs (comma-separated)
            const variantIds = variantIdsAttr.split(',').map(id => id.trim());
            
            // Check if current variant ID is in the list
            const shouldShow = variantIds.includes(variantIdStr);
            
            if (shouldShow) {
                // Show the wrapper by removing hidden class
                wrapper.classList.remove('hidden');
                // Ensure embla__slide class is added if it's in a carousel
                if (wrapper.closest('embla-carousel')) {
                    wrapper.classList.add('embla__slide');
                }
                // Remove any inline display styles that might hide it
                const currentStyle = wrapper.getAttribute('style') || '';
                const cleanedStyle = currentStyle.replace(/display\s*:\s*none[^;]*;?/gi, '').trim();
                if (cleanedStyle) {
                    wrapper.setAttribute('style', cleanedStyle);
                } else {
                    wrapper.removeAttribute('style');
                }
            } else {
                // Hide the wrapper by adding hidden class
                wrapper.classList.add('hidden');
                // Remove embla__slide class so Embla doesn't include it
                wrapper.classList.remove('embla__slide');
            }
        }
        
        reinitializeAffectedCarousels(imageWrappers) {
            // Reinitialize any Embla carousels that contain these images
            const carousels = document.querySelectorAll('embla-carousel');
            carousels.forEach(carousel => {
                // Check if this carousel contains any of the updated wrappers
                const containsUpdatedWrapper = Array.from(imageWrappers).some(wrapper => {
                    return carousel.contains(wrapper);
                });
                
                if (containsUpdatedWrapper && carousel.emblaApi && typeof carousel.handleBreakpointChange === 'function') {
                    // Use double RAF to ensure CSS class changes (hidden) are fully applied
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            carousel.handleBreakpointChange();
                        });
                    });
                }
            });
        }
        
        setupProductCardObserver() {
            // Watch for changes to product-card data-variant-id attributes
            this._productCardObserver = new MutationObserver((mutations) => {
                let shouldUpdate = false;
                
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'data-variant-id') {
                        shouldUpdate = true;
                    }
                    // Also check for newly added product-card elements
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            if (node.tagName && node.tagName.toLowerCase() === 'product-card') {
                                shouldUpdate = true;
                            } else if (node.querySelectorAll) {
                                const productCards = node.querySelectorAll('product-card[data-variant-id]');
                                if (productCards.length > 0) {
                                    shouldUpdate = true;
                                }
                            }
                        }
                    });
                });
                
                if (shouldUpdate) {
                    // Debounce updates to avoid excessive calls
                    if (this._productCardUpdateTimeout) {
                        clearTimeout(this._productCardUpdateTimeout);
                    }
                    this._productCardUpdateTimeout = setTimeout(() => {
                        this.updateProductCardMediaBlocks();
                        this._productCardUpdateTimeout = null;
                    }, 100);
                }
            });
            
            // Observe the document for product-card changes
            this._productCardObserver.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-variant-id'],
                childList: true,
                subtree: true
            });
        }

        // Public API methods
        setVariant(variantId) {
            if (this.variantSelector) {
                this.variantSelector.value = variantId;
                this.variantSelector.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        getSelectedQuantity() {
            const selectedQuantity = this.querySelector('input[name="quantity"]:checked');
            return selectedQuantity ? parseInt(selectedQuantity.value) : 1;
        }

        setQuantity(quantity) {
            const quantityInput = this.querySelector(`input[name="quantity"][value="${quantity}"]`);
            if (quantityInput) {
                quantityInput.checked = true;
                quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        getSelectedOptionsPublic() {
            return this.getSelectedOptions();
        }

        _updateAlternateBodyClass() {
            if (this.scopeContainer) return;
            document.body.classList.toggle('product-is-alternate', !!this._cachedIsRelatedVariant);
        }

        reset() {
            // Reset to first available options
            this.optionInputs.forEach(input => {
                if (input.dataset.optionAvailable === 'true') {
                    input.checked = true;
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                    return false; // Break after first
                }
            });
        }
    }

    customElements.define('product-options', ProductOptions);
}
